import { Form } from "react-bootstrap";
import { GenericToggle } from "@components/FormsV2/GenericToggle";
import { Select2V2 } from "@components/FormsV2";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import {
  FlagValue,
  FlagValueVariation,
  FlagValueVariationOption,
} from "app/javascript/src/types";
import { formatAccountDate } from "./formatAccountDate";

const variationKey = (value: FlagValueVariation) =>
  typeof value === "object" ? JSON.stringify(value) : String(value);

interface FeatureControlProps {
  flag: FlagValue;
  onChange: (variation: FlagValueVariation) => void;
}

const ToggleControl = ({ flag, onChange }: FeatureControlProps) => {
  const variations = flag.attributes.variations;
  const isBoolean = variations.every((v) => typeof v.value === "boolean");
  const [off, on] = isBoolean
    ? [
        variations.find((v) => v.value === false)!,
        variations.find((v) => v.value === true)!,
      ]
    : variations;
  return (
    <GenericToggle
      initialValueOfIsOn={
        variationKey(flag.attributes.variation) === variationKey(on.value)
      }
      onToggle={(isOn) => onChange(isOn ? on.value : off.value)}
      onText={on.name}
      offText={off.name}
      hasText={true}
    />
  );
};

const RadioControl = ({ flag, onChange }: FeatureControlProps) => {
  const currentKey = variationKey(flag.attributes.variation);
  return (
    <div className="featureRadios">
      {flag.attributes.variations.map((option: FlagValueVariationOption) => {
        const optionKey = variationKey(option.value);
        return (
          <Form.Check
            key={optionKey}
            type="radio"
            id={`${flag.id}-${optionKey}`}
            name={flag.id}
            label={option.name}
            checked={optionKey === currentKey}
            onChange={() => onChange(option.value)}
          />
        );
      })}
    </div>
  );
};

const SelectControl = ({ flag, onChange }: FeatureControlProps) => {
  const options = flag.attributes.variations.map((option) => ({
    label: option.name,
    value: variationKey(option.value),
    raw: option.value,
  }));
  const currentKey = variationKey(flag.attributes.variation);
  return (
    <div className="featureSelect">
      <Select2V2
        label=""
        value={options.find((o) => o.value === currentKey) || null}
        options={options}
        onChange={(selected) => onChange(selected.raw)}
      />
    </div>
  );
};

const statusLabel = (status: string) =>
  status
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

interface FeatureReleaseProps {
  flag: FlagValue;
  shortDateFormat?: string;
}

const FeatureRelease = ({
  flag,
  shortDateFormat,
}: FeatureReleaseProps): JSX.Element | null => {
  const status = flag.attributes.status;
  const date = formatAccountDate(
    flag.attributes["general-availability-date"] || "",
    shortDateFormat
  );

  if (!status && !date) return null;

  return (
    <div className="featureRelease">
      {status && (
        <span className={`featureBadge featureBadge--${status}`}>
          {statusLabel(status)}
        </span>
      )}
      {date && (
        <span className="featureReleaseDate">
          {`Generally available ${date}`}
        </span>
      )}
    </div>
  );
};

interface FeaturesListProps {
  flags: FlagValue[] | undefined;
  isLoading: boolean;
  onUpdate: (id: string, variation: FlagValueVariation) => void;
  shortDateFormat?: string;
}

const FeaturesList = ({
  flags,
  isLoading,
  onUpdate,
  shortDateFormat,
}: FeaturesListProps): JSX.Element => {
  if (isLoading) return <ScopeStackSpinner />;

  const sortedFlags = [...(flags || [])].sort((a, b) =>
    a.attributes.name.localeCompare(b.attributes.name)
  );

  const renderControl = (flag: FlagValue) => {
    const handleChange = (variation: FlagValueVariation) =>
      onUpdate(flag.id, variation);
    const count = flag.attributes.variations.length;
    if (count === 2)
      return <ToggleControl flag={flag} onChange={handleChange} />;
    if (count >= 3 && count <= 5)
      return <RadioControl flag={flag} onChange={handleChange} />;
    if (count > 5) return <SelectControl flag={flag} onChange={handleChange} />;
    return null;
  };

  if (sortedFlags.length === 0) return <p>No features available.</p>;

  return (
    <>
      {sortedFlags.map((flag) => (
        <div key={flag.id} className="featureRow">
          <div className="featureText">
            <div className="featureName">{flag.attributes.name}</div>
            <div className="featureDescription">
              {flag.attributes.description}
            </div>
            <FeatureRelease flag={flag} shortDateFormat={shortDateFormat} />
          </div>
          <div className="featureControl">{renderControl(flag)}</div>
        </div>
      ))}
    </>
  );
};

export default FeaturesList;
